/**
 * VersionCard — Footer version indicator with expandable changelog popup.
 *
 * Inspired by WayLucid's version card pattern:
 * - Always-visible footer button: "v1.2.0 Annihilation · Feb 18, 2026"
 * - Click to expand a floating changelog popup with latest changes
 * - "View full patch notes" links to the /updates page
 *
 * Uses the ClawWars design system (nw-red, military/terminal aesthetic).
 */
import { useState, useRef, useEffect } from "react";
import {
  CURRENT_VERSION,
  CURRENT_CODENAME,
  CURRENT_DATE,
  CHANGELOG,
  CATEGORY_CONFIG,
  getVersionChangeCount,
} from "@client/data/changelog";

const MAX_ITEMS_PER_CATEGORY = 3;

interface VersionCardProps {
  navigate: (page: any) => void;
}

export default function VersionCard({ navigate }: VersionCardProps) {
  const [isOpen, setIsOpen] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    function handleClick(e: MouseEvent) {
      if (
        popupRef.current &&
        !popupRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") setIsOpen(false);
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen]);

  const latestEntry = CHANGELOG[0];

  return (
    <>
      {/* Footer button — always visible */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center gap-1.5 text-white/30 hover:text-nw-red transition-colors text-sm cursor-pointer group"
        aria-expanded={isOpen}
        aria-haspopup="dialog"
      >
        <span className="font-mono text-xs">v{CURRENT_VERSION}</span>
        <span className="bg-gradient-to-r from-nw-red to-nw-amber bg-clip-text text-transparent font-bold font-heading text-xs">
          {CURRENT_CODENAME}
        </span>
        <span className="text-white/20">{"\u00B7"}</span>
        <span className="text-white/20 font-mono text-ui-xs lg:text-ui-sm">{CURRENT_DATE}</span>
        <svg
          className={`w-3 h-3 transition-transform duration-200 ${isOpen ? "" : "rotate-180"}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
        </svg>
      </button>

      {/* Expanded popup */}
      {isOpen && latestEntry && (
        <div
          ref={popupRef}
          className="fixed left-1/2 -translate-x-1/2 bottom-20 md:bottom-16 w-[calc(100vw-2rem)] max-w-96 rounded-xl border border-white/10 bg-[#0D0D0D]/95 backdrop-blur-xl shadow-2xl shadow-nw-red/10 overflow-hidden"
          role="dialog"
          aria-label="Version information"
          style={{ zIndex: 99999 }}
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-white/10 bg-gradient-to-r from-nw-red/10 to-nw-amber/10">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-white font-bold font-mono">v{latestEntry.version}</span>
                <span className="ml-2 bg-gradient-to-r from-nw-red to-nw-amber bg-clip-text text-transparent font-bold font-heading">
                  {latestEntry.codename}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {latestEntry.isLatest && (
                  <span className="px-2 py-0.5 rounded-full text-ui-2xs md:text-ui-xs font-bold uppercase tracking-wider bg-nw-red text-white">
                    Latest
                  </span>
                )}
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-white/40 hover:text-white/70 transition-colors p-1"
                  aria-label="Close"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <p className="text-white/40 text-xs mt-1 font-mono">
              {new Date(latestEntry.date).toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
            </p>
          </div>

          {/* Content */}
          <div className="px-4 py-3 max-h-64 overflow-y-auto space-y-3">
            {latestEntry.changes.map((group) => {
              const config = CATEGORY_CONFIG[group.category];
              if (!config) return null;
              const visibleItems = group.items.slice(0, MAX_ITEMS_PER_CATEGORY);
              const remaining = group.items.length - MAX_ITEMS_PER_CATEGORY;

              return (
                <div key={group.category}>
                  <h4
                    className="text-xs font-bold uppercase tracking-wider flex items-center gap-1 mb-1.5"
                    style={{ color: config.color }}
                  >
                    {config.icon} {config.label}
                  </h4>
                  <ul className="space-y-1">
                    {visibleItems.map((item, i) => (
                      <li
                        key={i}
                        className="text-white/60 text-xs leading-relaxed flex items-start gap-2"
                      >
                        <span className="text-white/20 mt-0.5">{"\u2022"}</span>
                        <span>{item}</span>
                      </li>
                    ))}
                    {remaining > 0 && (
                      <li className="text-white/30 text-xs italic">
                        +{remaining} more
                      </li>
                    )}
                  </ul>
                </div>
              );
            })}
          </div>

          {/* Footer link */}
          <div className="px-4 py-2.5 border-t border-white/10 bg-white/[0.02]">
            <button
              onClick={() => {
                setIsOpen(false);
                navigate({ type: "updates" });
              }}
              className="text-xs text-nw-red hover:text-red-400 transition-colors flex items-center gap-1 cursor-pointer font-mono"
            >
              View full patch notes
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
}
